import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import Translator from "@/utils/common/Translator.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {computed, Ref} from "vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import GenericReferenceFieldWidgetUtil from "@/utils/editView/widgets/GenericReferenceFieldWidgetUtil.ts";
import {DisplayedAllowedValue, OperatorOption, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import AllowedValue = namaTypes.AllowedValue;

export default class DropDownFieldWidgetUtil extends FieldWidgetUtil {
  private readonly _allowedValues: DisplayedAllowedValue[];
  private readonly translator: ChoiceTranslator;

  constructor(presenter: WidgetsPresenter, fieldMetaData: FieldMetaData, allowedValues?: namaTypes.AllowedValue[] | undefined) {
    super(presenter, fieldMetaData);
    this.translator = DropDownFieldWidgetUtil.createTranslator(fieldMetaData);
    this._allowedValues = DropDownFieldWidgetUtil.createSortedAllowedValues(GUIUtilities.getAllowedValues(presenter.entityType, fieldMetaData), fieldMetaData.enumTypeName!
      , this.translator);
    if (allowedValues && allowedValues.length)
      this._allowedValues = DropDownFieldWidgetUtil.createSortedAllowedValues(allowedValues, fieldMetaData.enumTypeName!, this.translator);
  }

  get allowedValues(): DisplayedAllowedValue[] {
    return this._allowedValues;
  }

  override formattedValue() {
    return computed(() => this.fetchFieldValue() && this.allowedValues ? this.allowedValues.find(l => l.id === this.fetchFieldValue()) : undefined);
  }

  onSelect = async (value?: DisplayedAllowedValue) => {
    let originalValue = this.fetchFieldValue();
    if (this.isEditOfDisabledFieldRejected(originalValue, value ? value.id : value))
      return;
    NamaCommon.setFieldValue(this.presenter.fetchRecord(), this.fieldId(), value ? value.id : value);
    if ((value && value.id !== originalValue) || originalValue) {
      const loadingStore = useLoadingStore();
      loadingStore.startLoading();
      if (this.presenter.fetchDispatcher())
        await this.presenter.fetchDispatcher().validate(this.fieldId())
      loadingStore.stopLoading();
    }
  }

  override onUserInputFinished = async () => {
    let inputFieldValue = this.inputFieldValueRef.value;
    let originalValue = this.fetchFieldValue();
    if (inputFieldValue === originalValue || (inputFieldValue && inputFieldValue.id === originalValue))
      return;
    let matched = inputFieldValue ? this._allowedValues.find(l => DropDownFieldWidgetUtil.isAllowedValueMatched(l, inputFieldValue)) : inputFieldValue;
    await this.onSelect(matched);
  }

  static isAllowedValueMatched(e: DisplayedAllowedValue | OperatorOption, val) {
    return e.searchValue && (e.searchValue.toLowerCase().includes(val)
      || e.searchValue.toLowerCase().includes(StringUtils.normalizeArabic(val))
      || e.searchValue.toLowerCase().includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(val)))
      || e.searchValue.toLowerCase().includes(StringUtils.arabicToEnglish(val).toLowerCase()));
  }


  static createSortedAllowedValues(allowedValues: any[], enumTypeName: string, translator: ChoiceTranslator, extraAllowedValues?: string[]) {
    let sortedAllowedValues: DisplayedAllowedValue[] = [];
    for (let allowedValue of allowedValues ?? []) {
      let strValue;
      let title;
      if (typeof allowedValue === 'object' && ((allowedValue as AllowedValue).title || (allowedValue as AllowedValue).id) && (allowedValue as AllowedValue).value) {
        strValue = (allowedValue as AllowedValue).value;
        title = Translator.translateTitledId(enumTypeName, allowedValue);
      } else {
        strValue = allowedValue;
        title = translator(enumTypeName, strValue);
      }
      sortedAllowedValues.push({id: strValue, title: title});
    }
    if (extraAllowedValues) {
      for (let extraAllowedValue of extraAllowedValues) {
        sortedAllowedValues.push({id: extraAllowedValue, title: translator(enumTypeName, extraAllowedValue)!});
      }
    }
    sortedAllowedValues.sort((a, b) => a.title.localeCompare(b.title));
    sortedAllowedValues.forEach(l => l.searchValue = EditScreenFieldUtils.fetchSearchTextForAllowedValue(l.id, enumTypeName));
    return sortedAllowedValues;
  }

  static createTranslator(fieldMetaData: FieldMetaData) {
    if (!fieldMetaData || !fieldMetaData.allowedValuesTitles || !fieldMetaData.allowedValuesTitles.map(l => l.title)
      .every(l => l && l.arabicText && l.englishText))
      return DropDownFieldWidgetUtil.DEFAULT_TRANSLATOR;
    return (enumName: string, allowedValue: string) => {
      let val = fieldMetaData.allowedValuesTitles?.find(l => allowedValue === l.id);
      if (val)
        return Translator.translateTitledId(undefined, val);
      return this.DEFAULT_TRANSLATOR(enumName, allowedValue);
    };

  }

  private static DEFAULT_TRANSLATOR: ChoiceTranslator = (enumName: string, allowedValue: string) => {
    if (!allowedValue)
      return "";
    if (!enumName)
      return Translator.translateId(allowedValue);
    return Translator.translateId(enumName + "." + allowedValue);
  };

  static dropDownFilterFn(options: Ref<DisplayedAllowedValue[]>, fieldUtil: GenericReferenceFieldWidgetUtil | DropDownFieldWidgetUtil, val, update) {
    update(async () => {
        if (!val) {
          options.value = fieldUtil.allowedValues;
          return;
        }
        options.value = fieldUtil.allowedValues.filter(e => DropDownFieldWidgetUtil.isAllowedValueMatched(e, val))
          .sort((a, b) => a.id === b.id ? 0 : a.searchValue!.length - b.searchValue!.length);
      },
      newRef => {
        if (val !== '' && options.value.length > 0) {
          setTimeout(() => {
            newRef.setOptionIndex(-1)
            newRef.moveOptionSelection(1, true)
          });
        }
      });
  }
}

export type ChoiceTranslator = (enumName: string, allowedValue: string) => string | undefined;
